import type { Ulid } from '@grademycv/shared-types';

/**
 * Admin SPA fetch wrapper — same Sanctum SPA cookie + XSRF pattern as
 * apps/web. Kept as a per-app copy so the admin bundle is independent
 * and the security-sensitive auth flow stays auditable in isolation.
 */

// Resolution order: runtime config (window.__APP_CONFIG__, rewritten
// per-container at start so one image serves every environment) →
// build-time Vite env → local-dev default.
const runtimeBase = (globalThis as unknown as { __APP_CONFIG__?: { apiBaseUrl?: string } })
  .__APP_CONFIG__?.apiBaseUrl;

const BASE =
  runtimeBase ??
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ??
  'http://localhost:8080/api/v1';
const SANCTUM_BASE = BASE.replace(/\/api\/v1\/?$/, '');

let csrfPrimed = false;

async function primeCsrf(): Promise<void> {
  if (csrfPrimed) return;
  await fetch(`${SANCTUM_BASE}/sanctum/csrf-cookie`, { credentials: 'include' });
  csrfPrimed = true;
}

/**
 * Fired on every 401 response, from any page's `api()` call — not just
 * hydrate's own on `/me`. A stale persisted session (e.g. a tab left open
 * across the server-side session lifetime) only surfaces as a 401 on
 * whatever the current page happens to fetch, so this is the one place
 * that can reliably clear the client-side session and send the user back
 * to sign-in instead of every page showing its own dead-end "Load failed".
 * Registered by the auth store (`lib/auth.ts`) to avoid a circular import.
 * 401 is reserved for "not authenticated" here — login/MFA failures use
 * 422, so this never fires mid sign-in and can't loop against /sign-in.
 */
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

function getCookie(name: string): string | null {
  const m = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&')}=([^;]*)`),
  );
  if (m === null) return null;
  const value = m[1];
  return value !== undefined ? decodeURIComponent(value) : null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    public readonly url: string,
  ) {
    super(
      typeof body === 'object' &&
        body !== null &&
        'message' in body &&
        typeof (body as { message: unknown }).message === 'string'
        ? (body as { message: string }).message
        : `HTTP ${status} from ${url}`,
    );
  }
}

export interface ApiRequestInit {
  method?: string;
  headers?: HeadersInit;
  json?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const url = new URL(BASE.replace(/\/$/, '') + (path.startsWith('/') ? path : `/${path}`));
  if (init.query !== undefined) {
    for (const [k, v] of Object.entries(init.query)) {
      if (v !== undefined && v !== null) {
        url.searchParams.set(k, String(v));
      }
    }
  }

  const method = (init.method ?? (init.json !== undefined ? 'POST' : 'GET')).toUpperCase();
  const isMutating = method !== 'GET' && method !== 'HEAD';

  if (isMutating) {
    await primeCsrf();
  }

  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.json !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (isMutating) {
    const xsrf = getCookie('XSRF-TOKEN');
    if (xsrf !== null) {
      headers.set('X-XSRF-TOKEN', xsrf);
    }
  }

  const fetchInit: RequestInit = { method, headers, credentials: 'include' };
  if (init.json !== undefined) {
    fetchInit.body = JSON.stringify(init.json);
  }
  if (init.signal !== undefined) {
    fetchInit.signal = init.signal;
  }

  const response = await fetch(url.toString(), fetchInit);

  const text = await response.text();
  const body = text.length > 0 ? safeJson(text) : null;

  if (!response.ok) {
    if (response.status === 401) {
      unauthorizedHandler?.();
    }
    throw new ApiError(response.status, body, url.toString());
  }

  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export type { Ulid };
